'use client';

/**
 * Monaiq Licensing Hook
 *
 * Client-side hook for checking feature entitlements.
 * Fetches license data from the AI engine's /licensing/features endpoint
 * and provides helpers for feature gating in components.
 *
 * @neutralaiz.com users get observeOnly=true — all features visible, usage tracked.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  useOptionalAccessControlState,
  useOptionalAppShell,
} from '@/components/shell/AppShellContext';
import { getLicenseCacheKey } from './context';
import type { FeatureKey, LicenseTier } from './types';

// Response shape from AI engine /licensing/features
interface LicenseResponse {
  licensed: boolean;
  observeOnly: boolean;
  tier: string;
  source: string;
  features: Record<string, boolean>;
  rateLimits: Record<string, { sampleSeconds: number; rateLimit: number }>;
  expiryDate?: string;
  classification?: string;
  error?: string;
  message?: string;
  simulated?: boolean;
}

export interface UseLicensingResult {
  /** Whether licensing data has loaded */
  loaded: boolean;
  /** Whether an active license exists */
  licensed: boolean;
  /** Observe-only mode (@neutralaiz.com) — all features allowed, usage tracked */
  observeOnly: boolean;
  /** Current tier (essentials, professional, enterprise, internal) */
  tier: LicenseTier | 'internal' | null;
  /** Where the license was resolved from (user, agency, department, observe-only, simulator) */
  source: string | null;
  /** Whether the current license data is from the tier simulator */
  simulated: boolean;
  /** Check if a specific feature is allowed */
  isFeatureAllowed: (featureKey: FeatureKey | string) => boolean;
  /** Get all allowed feature keys */
  allowedFeatures: string[];
  /** Get rate limit for a feature */
  getRateLimit: (featureKey: string) => { sampleSeconds: number; rateLimit: number } | null;
  /** Error if licensing check failed */
  error: string | null;
  /** Force refresh the license data */
  refresh: () => Promise<void>;
}

const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes
const MAX_CACHE_SIZE = 100;
const _responseCache = new Map<string, { response: LicenseResponse; timestamp: number }>();

function getCachedResponse(cacheKey: string): LicenseResponse | null {
  const cached = _responseCache.get(cacheKey);
  if (!cached) return null;
  if (Date.now() - cached.timestamp >= CACHE_TTL_MS) {
    _responseCache.delete(cacheKey);
    return null;
  }
  return cached.response;
}

function setCachedResponse(cacheKey: string, response: LicenseResponse) {
  if (_responseCache.size >= MAX_CACHE_SIZE) {
    const oldestKey = _responseCache.keys().next().value;
    if (oldestKey) {
      _responseCache.delete(oldestKey);
    }
  }

  _responseCache.set(cacheKey, {
    response,
    timestamp: Date.now(),
  });
}

function createFallbackResponse(): LicenseResponse {
  return {
    licensed: false,
    observeOnly: false,
    tier: 'unknown',
    source: 'none',
    features: {},
    rateLimits: {},
  };
}

export function useLicensing(): UseLicensingResult {
  const appShell = useOptionalAppShell();
  const accessControlState = useOptionalAccessControlState();
  const userId = appShell?.user?.id || 'anonymous';
  const authVersion = appShell?.authVersion ?? 0;
  const agencyId = accessControlState?.activeAgencyId ?? null;
  const departmentId = accessControlState?.activeDepartmentId ?? null;
  const cacheKey = getLicenseCacheKey(userId, { agencyId, departmentId });
  // #727: is the access-control context still resolving the active agency/dept
  // scope? If there's no provider (state null) there's nothing to wait for.
  const accessControlLoading = accessControlState ? accessControlState.loading : false;
  // Skip licensing fetch when unauthenticated — the /api/licensing/features
  // endpoint returns 401 for anonymous callers, producing a noisy console
  // error on marketing/login pages where we don't actually need license data.
  const isAuthenticated = !!appShell?.user?.id;
  const isAuthLoading = appShell?.isAuthLoading ?? false;

  const [data, setData] = useState<LicenseResponse | null>(() => getCachedResponse(cacheKey));
  const [loaded, setLoaded] = useState(() => getCachedResponse(cacheKey) !== null);
  const [error, setError] = useState<string | null>(null);
  const fetchingRef = useRef(false);

  const fetchLicense = useCallback(async () => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;

    try {
      const params = new URLSearchParams();
      if (agencyId) {
        params.set('agencyId', agencyId);
      }
      if (departmentId) {
        params.set('departmentId', departmentId);
      }

      const res = await fetch(
        `/api/licensing/features${params.size > 0 ? `?${params.toString()}` : ''}`,
        {
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        }
      );

      if (!res.ok) {
        setData(createFallbackResponse());
        setLoaded(true);
        setError(null);
        return;
      }

      const json: LicenseResponse = await res.json();
      setCachedResponse(cacheKey, json);
      setData(json);
      setError(json.error || null);
      setLoaded(true);
    } catch (err) {
      const cached = getCachedResponse(cacheKey);
      if (cached) {
        setData(cached);
      } else {
        setData(createFallbackResponse());
      }
      setError(err instanceof Error ? err.message : 'Failed to fetch license');
      setLoaded(true);
    } finally {
      fetchingRef.current = false;
    }
  }, [agencyId, cacheKey, departmentId]);

  useEffect(() => {
    const cached = getCachedResponse(cacheKey);
    setData(cached);
    setLoaded(cached !== null);
    setError(null);

    // Skip the fetch entirely for unauthenticated users. The auth check is
    // also still in flight when isAuthLoading is true — wait for that to
    // settle before deciding whether to fetch. Once authenticated, the
    // authVersion dep will re-run this effect and kick off the fetch.
    if (isAuthLoading) {
      return;
    }
    if (!isAuthenticated) {
      setData(createFallbackResponse());
      setLoaded(true);
      return;
    }

    // #727: wait for the access-control context to resolve the agency/department
    // scope before the first fetch, so we fetch ONCE with the correct scope
    // instead of once with null scope (then again when the scope resolves and
    // cacheKey changes). accessControlLoading is the dep that triggers the fetch
    // for no-org users (whose scope stays null, so cacheKey never changes).
    if (accessControlLoading) {
      return;
    }

    if (!cached) {
      fetchLicense();
    }
  }, [authVersion, cacheKey, fetchLicense, isAuthenticated, isAuthLoading, accessControlLoading]);

  useEffect(() => {
    const handler = () => {
      _responseCache.delete(cacheKey);
      fetchLicense();
    };

    window.addEventListener('tier-simulator-changed', handler);
    window.addEventListener('masquerade-changed', handler);

    return () => {
      window.removeEventListener('tier-simulator-changed', handler);
      window.removeEventListener('masquerade-changed', handler);
    };
  }, [cacheKey, fetchLicense]);

  const isFeatureAllowed = useCallback(
    (featureKey: FeatureKey | string): boolean => {
      if (!data) return true; // Not loaded yet — allow by default
      if (!data.licensed) return true; // No license configured — all features available
      if (data.observeOnly) return true; // @neutralaiz.com — all features allowed
      return data.features[featureKey] === true;
    },
    [data]
  );

  const getRateLimit = useCallback(
    (featureKey: string) => {
      if (!data?.rateLimits) return null;
      if (data.observeOnly) return null; // No rate limits for observe-only
      return data.rateLimits[featureKey] || null;
    },
    [data]
  );

  const allowedFeatures = data?.features
    ? Object.entries(data.features)
        .filter(([, allowed]) => allowed)
        .map(([key]) => key)
    : [];

  return {
    loaded,
    licensed: data?.licensed ?? false,
    observeOnly: data?.observeOnly ?? false,
    tier: (data?.tier as LicenseTier | 'internal') || null,
    source: data?.source || null,
    simulated: data?.simulated ?? false,
    isFeatureAllowed,
    allowedFeatures,
    getRateLimit,
    error,
    refresh: async () => {
      _responseCache.delete(cacheKey);
      await fetchLicense();
    },
  };
}
